import { type ApolloClient, gql } from '@apollo/client';

import type { MemberIndexViewableField } from '@/components/GlobalSearch/types';

import type {
  GetAIAskSearchData,
  GetAIAskSearchDataVariables,
} from '../../GetAIAskSearchData';
import type { AiSearchResultsPageState } from '../../types';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import { normalizeIndexBlocks } from '../../utils/normalizeIndexBlocks';
import { fetchAiSearchPageSections } from './indexRegistry';

export const GET_AI_ASK_SEARCH_DATA = gql`
  query GetAIAskSearchData($input: getAIAskSearchDataInput!) {
    getAIAskSearchData(input: $input) {
      queryToken
      tenant_id
      question_preview
      count
      created_at
      expires_at
      indexes {
        index
        object_ids
        count
      }
    }
  }
`;

export async function loadResultsState(
  apolloClient: ApolloClient<object>,
  queryToken: string,
  indexData: AiAskIndexData
): Promise<AiSearchResultsPageState> {
  const { data, error } = await apolloClient.query<
    GetAIAskSearchData,
    GetAIAskSearchDataVariables
  >({
    query: GET_AI_ASK_SEARCH_DATA,
    variables: { input: { queryToken } },
    fetchPolicy: 'network-only',
  });

  if (error) {
    throw error;
  }

  const searchData = data?.getAIAskSearchData;
  if (!searchData) {
    throw new Error('No search data returned.');
  }

  const indexBlocks = normalizeIndexBlocks(searchData);
  if (indexBlocks.length === 0) {
    throw new Error('No index results returned.');
  }

  const memberViewableField =
    indexData.memberIndexViewableField as MemberIndexViewableField;

  const { sections, skippedIndexes } = await fetchAiSearchPageSections(
    apolloClient,
    indexBlocks,
    memberViewableField
  );

  if (sections.length === 0) {
    const unsupportedIndexLabels = skippedIndexes.length
      ? skippedIndexes.join(', ')
      : indexBlocks.map((indexBlock) => indexBlock.index).join(', ');
    throw new Error(
      `No supported index results to display (${unsupportedIndexLabels}).`
    );
  }

  return {
    questionPreview: searchData.question_preview,
    totalCount: searchData.count,
    idsTruncated: Boolean(
      (searchData as { ids_truncated?: boolean | null }).ids_truncated
    ),
    skippedIndexes,
    sections,
  };
}
